import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cognitoUserManager, userHasSuperadminGroup } from '@/config/authConfig';
import { syncSentryUser } from '@/config/sentry';
import { AUTH_STATE } from '@/types';
import { User } from 'oidc-client-ts';
import AuthService from '@/services/auth.service';
import UsersService from '@/services/users.service';
import { AuthContext } from '@/shared/AuthContext';

export const AuthProvider = ({ children }: { children: ReactNode | ReactNode[] }) => {
  const [state, setAuthenticated] = useState<AUTH_STATE>(AUTH_STATE.LOADING);
  const [userInfo, setUserInfo] = useState<User | null>(null);
  const isLoggingOutRef = useRef<boolean>(false);

  const rejectNonSuperadminUser = useCallback(() => {
    isLoggingOutRef.current = true;
    cognitoUserManager.stopSilentRenew();
    setUserInfo(null);
    setAuthenticated(AUTH_STATE.UNAUTHENTICATED);
    AuthService.signOut();
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    isLoggingOutRef.current = true;
    cognitoUserManager.stopSilentRenew();
    await AuthService.signOut();
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const user = await UsersService.getUser();
      if (cancelled) return;
      if (user && !userHasSuperadminGroup(user)) {
        rejectNonSuperadminUser();
        return;
      }
      setUserInfo(user);
      if (user) {
        setAuthenticated(AUTH_STATE.AUTHENTICATED);
      } else {
        setAuthenticated(AUTH_STATE.UNAUTHENTICATED);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rejectNonSuperadminUser]);

  useEffect(() => {
    syncSentryUser({ user: userInfo });
  }, [userInfo]);

  useEffect(() => {
    const handleUserLoaded = (user: User) => {
      if (isLoggingOutRef.current) return;
      if (!userHasSuperadminGroup(user)) {
        rejectNonSuperadminUser();
        return;
      }
      setUserInfo(user);
      setAuthenticated(AUTH_STATE.AUTHENTICATED);
    };

    const handleUserUnloaded = () => {
      if (isLoggingOutRef.current) return;
      setUserInfo(null);
      setAuthenticated(AUTH_STATE.UNAUTHENTICATED);
    };

    cognitoUserManager.events.addUserLoaded(handleUserLoaded);
    cognitoUserManager.events.addUserUnloaded(handleUserUnloaded);

    return () => {
      cognitoUserManager.events.removeUserLoaded(handleUserLoaded);
      cognitoUserManager.events.removeUserUnloaded(handleUserUnloaded);
    };
  }, [rejectNonSuperadminUser]);

  return (
    <AuthContext.Provider
      value={{
        state,
        logout,
        userManager: cognitoUserManager,
        setAuthenticated,
        userInfo,
        setUserInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
